import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, X, Link2, FileText, Wrench, UserCheck } from "lucide-react";
import { toast } from "sonner";
import CommentThread from "./CommentThread";
import api from "@/lib/axios";

const STATUS_OPTIONS = [
  "Open",
  "Submitted",
  "Under Review",
  "Approved",
  "Assigned",
  "In Progress",
  "On Hold",
  "Waiting Parts",
  "Waiting Vendor",
  "Completed",
  "Closed",
  "Cancelled",
];

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n)
    ? new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)
    : "£0.00";
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

const TaskDetailModal = ({ taskId, forceOpen = false, onOpenChange, onUpdated }) => {
  const [open, setOpen] = useState(forceOpen);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);

  const [statusValue, setStatusValue] = useState("");
  const [note, setNote] = useState("");
  const [actualCost, setActualCost] = useState("");
  const [saving, setSaving] = useState(false);

  const handleModalOpen = (val) => {
    setOpen(val);
    onOpenChange?.(val);
  };

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/solo-mdsh-api/maintenance/tasks/${taskId}`);
      setTask(data);
      setStatusValue(data?.status || "Open");
      setActualCost(
        data?.actual_cost !== null && data?.actual_cost !== undefined
          ? String(data.actual_cost)
          : ""
      );
    } catch (err) {
      console.error("❌ Error fetching task:", err);
      toast.error("Failed to fetch task");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !taskId) return;
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  const handleUpdate = async () => {
    try {
      setSaving(true);

      const payload = {
        status: statusValue || task?.status,
        update_notes: note || undefined,
        actual_cost: actualCost === "" ? undefined : Number(actualCost),
      };

      const { data } = await api.put(`/solo-mdsh-api/maintenance/update/${taskId}`, payload);

      setTask(data);
      setStatusValue(data?.status || "Open");
      setActualCost(
        data?.actual_cost !== null && data?.actual_cost !== undefined
          ? String(data.actual_cost)
          : ""
      );
      setNote("");

      toast.success("✅ Task updated");
      onUpdated?.(data);
    } catch (err) {
      console.error("❌ Update failed:", err);
      const msg = err?.response?.data?.error || "Failed to update task";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const createdAt = useMemo(
    () => (task?.created_at ? new Date(task.created_at).toLocaleString("en-GB") : "—"),
    [task?.created_at]
  );

  const updatedAt = useMemo(
    () => (task?.updated_at ? new Date(task.updated_at).toLocaleString("en-GB") : "—"),
    [task?.updated_at]
  );

  const resolvedAt = useMemo(
    () => (task?.resolved_at ? new Date(task.resolved_at).toLocaleString("en-GB") : "—"),
    [task?.resolved_at]
  );

  const assignments = safeArray(task?.assignments);
  const history = safeArray(task?.history);
  const comments = safeArray(task?.comments);
  const linkedPOs = safeArray(task?.linked_purchase_orders);

  return (
    <Dialog open={open} onOpenChange={handleModalOpen}>
      {!forceOpen && (
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            Details
          </Button>
        </DialogTrigger>
      )}

      {open && (
        <DialogContent className="relative bg-[#111] text-white border border-neutral-700 max-w-4xl p-6 space-y-6 rounded-xl max-h-[90vh] overflow-y-auto">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 text-slate-400 hover:text-red-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div>
            <h3 className="text-2xl font-bold">📝 Task Detail</h3>
            <p className="text-sm text-slate-400 mt-1">
              Review, progress, and audit the maintenance request.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : task ? (
            <div className="space-y-6 text-sm">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-neutral-900 border border-neutral-700 rounded-xl p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <strong>Task ID:</strong> #{task.task_id}
                    </div>
                    <div>
                      <strong>Task Number:</strong> {task.task_number || "—"}
                    </div>
                    <div>
                      <strong>Issue:</strong> {task.issue_type}
                    </div>
                    <div>
                      <strong>Status:</strong>{" "}
                      <span className="text-yellow-400">{task.status || "—"}</span>
                    </div>
                    <div>
                      <strong>Priority:</strong>{" "}
                      <span className="text-orange-400">{task.priority || "—"}</span>
                    </div>
                    <div>
                      <strong>Urgency:</strong>{" "}
                      <span className="text-red-400">{task.urgency_level || "—"}</span>
                    </div>
                    <div>
                      <strong>Request Type:</strong> {task.request_type || "—"}
                    </div>
                    <div>
                      <strong>Source:</strong> {task.source_channel || "—"}
                    </div>
                    <div>
                      <strong>Location:</strong> {task.location || "—"}
                    </div>
                    <div>
                      <strong>Asset No.:</strong> {task.asset_number || "—"}
                    </div>
                    <div>
                      <strong>Asset Name:</strong> {task.asset_name || "—"}
                    </div>
                    <div>
                      <strong>Asset Type:</strong> {task.asset_type || "—"}
                    </div>
                    <div>
                      <strong>Vendor:</strong> {task.vendor_name || "—"}
                    </div>
                    <div>
                      <strong>Cost Centre:</strong> {task.cost_centre_name || "—"}
                    </div>
                    <div>
                      <strong>Estimated Cost:</strong> {formatMoney(task.cost_estimate)}
                    </div>
                    <div>
                      <strong>Actual Cost:</strong> {formatMoney(task.actual_cost)}
                    </div>
                  </div>

                  <div>
                    <strong>Description:</strong>
                    <p className="text-slate-300 mt-1 whitespace-pre-wrap">{task.description}</p>
                  </div>

                  {task.update_notes ? (
                    <div className="bg-neutral-800 p-3 rounded text-white border border-neutral-600">
                      <strong className="text-blue-400 block mb-1">🛠 Last Engineer Note:</strong>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{task.update_notes}</p>
                    </div>
                  ) : null}
                </div>

                <div className="bg-neutral-900 border border-neutral-700 rounded-xl p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <strong>Created:</strong> {createdAt}
                    </div>
                    <div>
                      <strong>Updated:</strong> {updatedAt}
                    </div>
                    <div>
                      <strong>Resolved:</strong> {resolvedAt}
                    </div>
                    <div>
                      <strong>Target Completion:</strong>{" "}
                      {task.target_completion_at
                        ? new Date(task.target_completion_at).toLocaleString("en-GB")
                        : "—"}
                    </div>
                    <div>
                      <strong>Reported By:</strong> {task.reported_by_name || "—"}
                    </div>
                    <div>
                      <strong>Updated By:</strong> {task.updated_by_name || "—"}
                    </div>
                    <div>
                      <strong>Vendor Required:</strong> {task.requires_vendor ? "Yes" : "No"}
                    </div>
                    <div>
                      <strong>PO Required:</strong> {task.requires_po ? "Yes" : "No"}
                    </div>
                    <div>
                      <strong>Chargeable:</strong> {task.is_chargeable ? "Yes" : "No"}
                    </div>
                    <div>
                      <strong>Breakdown:</strong> {task.is_breakdown ? "Yes" : "No"}
                    </div>
                  </div>

                  <div className="border-t border-neutral-700 pt-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <select
                        className="bg-neutral-800 text-white border border-neutral-700 p-2 rounded"
                        value={statusValue}
                        onChange={(e) => setStatusValue(e.target.value)}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>

                      <input
                        type="number"
                        step="0.01"
                        placeholder="Actual cost"
                        value={actualCost}
                        onChange={(e) => setActualCost(e.target.value)}
                        className="bg-neutral-800 text-white border border-neutral-700 p-2 rounded"
                      />
                    </div>

                    <Textarea
                      placeholder="Add new update notes"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="bg-neutral-800 text-white"
                    />

                    <Button
                      onClick={handleUpdate}
                      disabled={saving}
                      className="w-full bg-blue-700 hover:bg-blue-600"
                    >
                      {saving ? "Saving..." : "💾 Save Update"}
                    </Button>
                  </div>
                </div>
              </div>

              {task.related_po_id ? (
                <div className="bg-neutral-900 p-4 border border-blue-800 rounded-md text-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <strong className="text-blue-400 flex items-center gap-2">
                      <Link2 size={16} />
                      Linked Purchase Order
                    </strong>
                    <a
                      href={`/admin-procurement/purchase-order/${task.related_po_id}`}
                      className="text-blue-500 underline text-xs"
                    >
                      View PO ↗
                    </a>
                  </div>
                  <div>
                    <strong>PO ID:</strong> {task.related_po_id}
                  </div>
                  <div>
                    <strong>PO Number:</strong> {task.po_number || "—"}
                  </div>
                  <div>
                    <strong>PO Status:</strong> {task.po_status || "—"}
                  </div>
                  <div>
                    <strong>PO Notes:</strong> {task.po_notes || "—"}
                  </div>
                </div>
              ) : null}

              {linkedPOs.length > 0 ? (
                <div className="bg-neutral-900 p-4 border border-neutral-700 rounded-xl">
                  <div className="font-semibold text-white mb-3 flex items-center gap-2">
                    <FileText size={16} />
                    Linked Purchase Orders
                  </div>
                  <div className="space-y-2">
                    {linkedPOs.map((row) => (
                      <div
                        key={row.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-800 bg-neutral-950 p-3"
                      >
                        <div>
                          <div className="font-medium">{row.po_number || `PO #${row.po_id}`}</div>
                          <div className="text-xs text-slate-400">
                            {row.link_type || "linked"} • {row.status || "—"}
                          </div>
                        </div>
                        <a
                          href={`/admin-procurement/purchase-order/${row.po_id}`}
                          className="text-blue-400 underline text-xs"
                        >
                          Open ↗
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-neutral-900 p-4 border border-neutral-700 rounded-xl">
                  <div className="font-semibold text-white mb-3 flex items-center gap-2">
                    <UserCheck size={16} />
                    Assignments
                  </div>

                  {assignments.length === 0 ? (
                    <p className="text-slate-400">No active assignments found.</p>
                  ) : (
                    <div className="space-y-2">
                      {assignments.map((row) => (
                        <div key={row.assignment_id} className="rounded-lg border border-neutral-800 bg-neutral-950 p-3">
                          <div className="font-medium">{row.assigned_to_name || "—"}</div>
                          <div className="text-xs text-slate-400">
                            {row.assignment_role || row.assignment_type || "Assigned"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-neutral-900 p-4 border border-neutral-700 rounded-xl">
                  <div className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Wrench size={16} />
                    Cost Summary
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <strong>Lines:</strong> {task?.cost_summary?.line_count ?? 0}
                    </div>
                    <div>
                      <strong>Total:</strong> {formatMoney(task?.cost_summary?.total_cost)}
                    </div>
                    <div>
                      <strong>Approved:</strong> {formatMoney(task?.cost_summary?.approved_cost)}
                    </div>
                    <div>
                      <strong>Posted:</strong> {formatMoney(task?.cost_summary?.posted_cost)}
                    </div>
                    <div>
                      <strong>PO Cost:</strong> {formatMoney(task?.cost_summary?.po_cost)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-900 p-4 border border-neutral-700 rounded-xl">
                <div className="font-semibold text-white mb-3">History</div>
                {history.length === 0 ? (
                  <p className="text-slate-400">No history records found.</p>
                ) : (
                  <div className="space-y-2">
                    {history.slice(0, 20).map((row) => (
                      <div key={row.history_id} className="rounded-lg border border-neutral-800 bg-neutral-950 p-3">
                        <div className="font-medium">{row.action_type || "Update"}</div>
                        <div className="text-xs text-slate-400">
                          {row.actor_name_snapshot || "System"} •{" "}
                          {row.created_at ? new Date(row.created_at).toLocaleString("en-GB") : "—"}
                        </div>
                        {row.note ? <div className="text-sm text-slate-300 mt-1">{row.note}</div> : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-neutral-700">
                <CommentThread taskId={taskId} />
                {comments.length > 0 ? (
                  <div className="mt-4 space-y-2">
                    {comments.slice(0, 10).map((row) => (
                      <div key={row.comment_id} className="rounded-lg border border-neutral-800 bg-neutral-950 p-3">
                        <div className="text-sm text-white">{row.comment_text}</div>
                        <div className="text-xs text-slate-400 mt-1">
                          {row.staff_name || "—"} •{" "}
                          {row.created_at ? new Date(row.created_at).toLocaleString("en-GB") : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-red-400">⚠️ Task not found</p>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default TaskDetailModal;